import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, MapPin, BadgeCheck, Clock, Clock3, FileCheck, Tag } from "lucide-react";
import Seo from "@/components/Seo";
import CoverageChecker from "@/components/CoverageChecker";
import ServiceGrid from "@/components/ServiceGrid";
import Reviews from "@/components/Reviews";
import Faq from "@/components/Faq";
import { MaskLines, Reveal, Counter, SectionHead } from "@/components/Reveal";
import { CITIES, SERVICES, HOME_FAQS, HERO_BG, cityImage } from "@/data";
import { INSIGHTS } from "@/data/insights";

const FEATURED = ["cardiff", "swansea", "newport", "wrexham", "bangor", "carmarthen", "llandudno", "aberystwyth"];
const MARQUEE = [...SERVICES.map((s) => s.name), ...SERVICES.map((s) => s.name)];
const fmt = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

const Home = () => {
  const { scrollY } = useScroll();
  const xShift = useTransform(scrollY, [0, 800], [0, 120]);
  const xOpacity = useTransform(scrollY, [0, 600], [1, 0.15]);

  return (
    <div data-testid="home-page">
      <Seo
        title="Security Guards in Wales | Cardiff, Swansea & Newport | Wales Security"
        description="Security guards, mobile patrols and event security for Welsh businesses. Discuss coverage in Cardiff, Swansea, Newport and across Wales."
        path="/"
        altCy="/cy"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: HOME_FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }}
      />

      <section className="xp-noise relative flex min-h-[92vh] items-center overflow-hidden pt-16" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Welsh city skyline at dusk protected by Xtreme Protection security services" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-[#0A0C10]/60" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0A0C10] to-transparent" />
        </div>
        <motion.span
          style={{ x: xShift, opacity: xOpacity }}
          aria-hidden="true"
          className="outline-x font-condensed pointer-events-none absolute -right-10 top-1/2 hidden -translate-y-1/2 select-none text-[42vw] font-bold leading-none lg:block"
        >
          X
        </motion.span>
        <div className="relative mx-auto w-full max-w-7xl px-4 py-24 sm:px-6">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="eyebrow flex items-center gap-3">
            <span className="h-px w-10 bg-[#F5A800]" />
            Wales Security Operations // SIA Licensed
          </motion.p>
          <MaskLines
            className="font-condensed mt-6 text-5xl font-bold uppercase leading-[0.9] tracking-tight text-white sm:text-7xl lg:text-8xl"
            lines={["Security Guard", "Services", "Across Wales"]}
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-[#C8CCD2] sm:text-lg"
          >
            SIA-licensed security guards in Cardiff, Swansea and Newport — mobile patrols across Wales, event security for Cardiff's biggest nights, and rapid response teams in{" "}
            <span className="font-semibold text-[#F5A800]">35+ Welsh towns and cities</span>. Engineered deterrence, verified on every shift.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.75 }} className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/locations"
              data-testid="hero-explore-locations-btn"
              className="group flex items-center gap-2 bg-[#F5A800] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors duration-300 hover:bg-white"
            >
              Explore 35+ Wales Locations
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-view-services-btn"
              className="flex items-center gap-2 border border-white/25 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:border-[#F5A800] hover:text-[#F5A800]"
            >
              View All Security Services
            </Link>
          </motion.div>
        </div>
      </section>

      <div className="overflow-hidden border-y border-[#1C2027] bg-[#0D1015] py-4" data-testid="services-marquee">
        <div className="animate-marquee-slow flex w-max items-center gap-8">
          {MARQUEE.map((name, i) => (
            <span key={i} className="flex items-center gap-8 whitespace-nowrap">
              <span className="font-condensed text-lg font-semibold uppercase tracking-[0.2em] text-white/50">{name}</span>
              <span className="h-1.5 w-1.5 rotate-45 bg-[#F5A800]/60" />
            </span>
          ))}
        </div>
      </div>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
        <div className="grid items-start gap-10 lg:grid-cols-2">
          <Reveal>
            <CoverageChecker />
          </Reveal>
          <div className="grid grid-cols-2 gap-px border border-[#1C2027] bg-[#1C2027]" data-testid="stats-grid">
            {[
              { v: 30, s: "+", l: "Welsh towns & cities covered" },
              { v: 17, s: "", l: "Specialist security services" },
              { v: 24, s: "/7", l: "Wales command & response" },
              { v: 100, s: "%", l: "SIA licensed & BS 7858 vetted" },
            ].map((st, i) => (
              <Reveal key={st.l} delay={i * 0.08} className="h-full">
                <div className="flex h-full flex-col justify-between bg-[#10131A] p-6">
                  <Counter to={st.v} suffix={st.s} className="font-condensed text-5xl font-bold text-[#F5A800]" />
                  <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">{st.l}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#1C2027] bg-[#0D1015] py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
          <div>
            <SectionHead index="01" eyebrow="The Standard" title="Discipline is the deterrent" testid="standard-heading" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-[#9BA1AB]">
                Xtreme Protection exists because too much of the security industry runs on promises instead of proof. We run on proof: SIA-licensed officers vetted to BS 7858, GPS and NFC-verified patrols, time-stamped photographic reports and contract managers who answer for performance every single month.
              </p>
              <p className="mt-4 text-base leading-relaxed text-[#9BA1AB]">
                From a single night guard in Aberystwyth to a multi-site portfolio across the M4 corridor, every Welsh contract starts the same way — a free risk assessment, honest recommendations, and a written plan we can be held to.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 flex flex-wrap gap-2">
                {[
                  { icon: ShieldCheck, t: "SIA Licensed" },
                  { icon: BadgeCheck, t: "BS 7858 Vetted" },
                  { icon: Clock, t: "24/7 Response" },
                  { icon: FileCheck, t: "Verified Reporting" },
                ].map((b) => (
                  <span key={b.t} className="flex items-center gap-2 border border-[#F5A800]/30 bg-[#F5A800]/5 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#F5A800]">
                    <b.icon className="h-3.5 w-3.5" />
                    {b.t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="clip-frame relative">
              <img src={cityImage(3)} alt="SIA-licensed Xtreme Protection officer on duty in Wales" className="h-[420px] w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C10]/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 border-l-2 border-[#F5A800] pl-4">
                <p className="font-condensed text-lg font-bold uppercase text-white">Officer on post</p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-white/60">Verified patrol — South Wales</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <SectionHead index="02" eyebrow="The Arsenal" title="17 specialist services. One accountable partner." testid="services-heading" />
            <Reveal delay={0.1}>
              <Link to="/services" data-testid="home-all-services-btn" className="group flex items-center gap-2 border border-[#F5A800]/50 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-[#F5A800] transition-colors duration-300 hover:bg-[#F5A800] hover:text-[#0A0C10]">
                All Services <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#9BA1AB]">
            Manned guarding, door supervision, CCTV monitoring, mobile patrols, event security, close protection, K9 units, key holding and more — layered into packages that match your actual risk, not a sales template.
          </p>
          <div className="mt-10">
            <ServiceGrid />
          </div>
        </div>
      </section>

      <section className="border-t border-[#1C2027] bg-[#0D1015] py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
            <div>
              <SectionHead index="03" eyebrow="The Territory" title="Every Welsh region. One command." testid="territory-heading" />
              <Reveal delay={0.1}>
                <p className="mt-6 text-base leading-relaxed text-[#9BA1AB]">
                  National resources with genuinely local teams. Our regional command structure means the officer at your gate knows your streets, your police liaison team and your local risk landscape — backed by a 24/7 Wales command centre.
                </p>
                <Link to="/locations" data-testid="home-full-directory-btn" className="group mt-8 inline-flex items-center gap-2 border border-[#F5A800]/50 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-[#F5A800] transition-colors duration-300 hover:bg-[#F5A800] hover:text-[#0A0C10]">
                  Full Wales Directory <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
            <div className="grid gap-px border border-[#1C2027] bg-[#1C2027] sm:grid-cols-2">
              {FEATURED.map((slug, i) => {
                const c = CITIES.find((x) => x.slug === slug);
                return (
                  <Reveal key={slug} delay={(i % 2) * 0.08} y={14} className="h-full">
                    <Link to={`/locations/${c.slug}`} data-testid={`home-city-card-${slug}`} className="group flex h-full items-center justify-between bg-[#10131A] p-5 transition-colors duration-300 hover:bg-[#161A22]">
                      <div>
                        <p className="font-condensed text-lg font-bold uppercase tracking-wide text-white transition-colors duration-300 group-hover:text-[#F5A800]">Security {c.name}</p>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">{c.county}</p>
                      </div>
                      <MapPin className="h-4 w-4 text-[#F5A800]/60 transition-transform duration-300 group-hover:scale-125" />
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <Reviews />

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <SectionHead index="04" eyebrow="The Briefing" title="Security questions, answered" testid="faq-heading" />
          <div className="mt-10">
            <Faq items={HOME_FAQS} idPrefix="home-faq" />
          </div>
        </div>
      </section>

      <section className="border-t border-[#1C2027] bg-[#0D1015] py-16 lg:py-24" data-testid="home-insights-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <SectionHead index="05" eyebrow="Insights" title="Welsh security news & advice" testid="insights-heading" />
            <Reveal delay={0.1}>
              <Link to="/insights" data-testid="home-all-insights-btn" className="group flex items-center gap-2 border border-[#F5A800]/50 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-[#F5A800] transition-colors duration-300 hover:bg-[#F5A800] hover:text-[#0A0C10]">
                All Insights <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3" data-testid="home-insights-grid">
            {INSIGHTS.slice(0, 3).map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.08} y={18} className="h-full">
                <Link to={`/insights/${a.slug}`} data-testid={`home-insight-${a.slug}`} className="group flex h-full flex-col border border-[#1C2027] bg-[#10131A] transition-colors duration-300 hover:border-[#F5A800]/50">
                  <div className="relative h-40 overflow-hidden">
                    <img src={a.image} alt={a.title} className="h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#10131A] to-transparent" />
                    <span className="absolute left-4 top-4 flex items-center gap-1.5 bg-[#0A0C10]/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#F5A800]">
                      <Tag className="h-3 w-3" />
                      {a.category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col justify-between p-6">
                    <h3 className="font-condensed text-xl font-bold uppercase leading-tight tracking-wide text-white transition-colors duration-300 group-hover:text-[#F5A800]">{a.title}</h3>
                    <div className="mt-5 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">
                      <span>{fmt(a.date)}</span>
                      <span className="flex items-center gap-1.5 transition-colors duration-300 group-hover:text-[#F5A800]">
                        <Clock3 className="h-3 w-3" />
                        {a.read} min
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#1C2027] bg-[#F5A800] py-14" data-testid="cta-band">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 lg:flex-row lg:items-center">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#0A0C10]/60">Free risk assessment — no obligation</p>
            <h2 className="font-condensed mt-2 text-3xl font-bold uppercase leading-none text-[#0A0C10] sm:text-4xl">Secure your Welsh site today</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="tel:+442046402840" data-testid="cta-call-btn" className="bg-[#0A0C10] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#F5A800] transition-colors duration-300 hover:bg-[#1A1E26]">
              Call +44 2046 402840
            </a>
            <a href="mailto:admin@xtremeprotection.uk" data-testid="cta-email-btn" className="border-2 border-[#0A0C10] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors duration-300 hover:bg-[#0A0C10] hover:text-[#F5A800]">
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
